<template>
  <div class="sf-page">
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <div v-if="job != null">
      <v-snackbar right
                  top
                  v-model="snackbar">
        {{ snackbarText }}
      </v-snackbar>
      <top-header v-bind:subtitle="job.subject"
                  v-bind:title="job.templateName">
        <v-btn class="sf-back-btn"
               exact
               slot="left-button"
               text
               v-bind:to="{ name: 'administration-email-jobs' }">
          <v-icon left>ti-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-menu offset-y
                left
                slot="menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary"
                   outlined
                   v-bind="attrs"
                   v-on="on">
              {{ $t('actions') }}
              <v-icon right>ti-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-bind:disabled="job.failed === 0"
                         v-on:click="retry">
              <v-list-item-icon class="mr-3"><v-icon>ti-rotate-clockwise</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t('retryFailed') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-bind:disabled="job.pending === 0"
                         v-on:click="cancel">
              <v-list-item-icon class="mr-3"><v-icon>ti-ban</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t('cancel') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-on:click="load">
              <v-list-item-icon class="mr-3"><v-icon>ti-refresh</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t('refresh') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </top-header>
      <email-test-mode-banner />
      <div class="sf-container">
        <v-row>
          <v-col cols="12"
                 md="4">
            <v-card class="sf-card"
                    flat>
              <v-card-text class="sf-card__body">
                <div class="d-flex align-center flex-wrap mb-4"
                     style="gap: 8px 12px;">
                  <email-job-status-chip v-bind:status="job.status" />
                  <span class="text-caption sf-muted">{{ job.kind === 'Transactional' ? $t('transactional') : $t('campaign') }} · {{ job.audience === 'Supplier' ? $t('suppliers') : $t('customers') }}</span>
                </div>
                <v-progress-linear class="sf-progress mb-5"
                                   color="accent"
                                   height="8"
                                   rounded
                                   v-bind:value="job.total === 0 ? 100 : (job.sent + job.failed + job.skipped) / job.total * 100" />
                <v-simple-table class="sf-kv"
                                dense>
                  <tbody>
                    <tr><td>{{ $t('subject') }}</td><td>{{ job.subject }}</td></tr>
                    <tr><td>{{ $t('created') }}</td><td>{{ job.created | formatDate('L LT') }}</td></tr>
                    <tr><td>{{ $t('completed') }}</td><td>{{ job.completed | formatDate('L LT') }}</td></tr>
                    <tr><td>{{ $t('createdBy') }}</td><td class="text-caption">{{ job.createdBy }}</td></tr>
                    <tr><td>{{ $t('total') }}</td><td>{{ job.total }}</td></tr>
                    <tr><td>{{ $t('sent') }}</td><td class="success--text">{{ job.sent }}</td></tr>
                    <tr><td>{{ $t('failed') }}</td><td v-bind:class="{ 'error--text': job.failed > 0 }">{{ job.failed }}</td></tr>
                    <tr><td>{{ $t('skipped') }}</td><td>{{ job.skipped }}</td></tr>
                    <tr><td>{{ $t('pending') }}</td><td>{{ job.pending }}</td></tr>
                    <tr v-if="job.changeToState"><td>{{ $t('changeToState') }}</td><td>{{ job.changeToState }}</td></tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12"
                 md="8">
            <v-card class="sf-card"
                    flat>
              <div class="sf-card__header">
                <h2 class="sf-card__title">{{ $t('deliveries') }}</h2>
                <div class="sf-card__tools">
                  <v-select class="sf-search sf-job__filter"
                            clearable
                            dense
                            flat
                            hide-details
                            solo
                            v-bind:items="statusItems"
                            v-bind:placeholder="$t('status')"
                            v-model="statusFilter"
                            v-on:change="loadDeliveries" />
                </div>
              </div>
              <v-data-table dense
                            v-bind:footer-props="{ itemsPerPageOptions: [ 25, 50, 100, -1 ] }"
                            v-bind:headers="headers"
                            v-bind:items="deliveries"
                            v-bind:items-per-page="50"
                            v-bind:loading="deliveriesLoading"
                            v-bind:no-data-text="$t('noDeliveries')">
                <template v-slot:item.recipientType="{ item }">
                  {{ $t(item.recipientType) }}<span v-if="item.guestSlot > 0"> {{ item.guestSlot }}</span>
                </template>
                <template v-slot:item.status="{ item }">
                  <email-job-status-chip v-bind:status="item.status" />
                </template>
                <template v-slot:item.sentAt="{ item }">
                  {{ item.sentAt | formatDate('L LT') }}
                </template>
                <template v-slot:item.lastError="{ item }">
                  <span class="error--text text-caption"
                        v-bind:title="item.lastError">{{ item.lastError | truncate(60) }}</span>
                  <span class="text-caption"
                        v-if="item.status === 'Pending' && item.nextAttempt"> · {{ $t('nextAttempt') }} {{ item.nextAttempt | formatDate('LT') }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
  import EmailJobStatusChip from '@/components/administration/EmailJobStatusChip'
  import EmailTestModeBanner from '@/components/administration/EmailTestModeBanner'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    beforeDestroy() {
      clearInterval(this.timer)
    },
    components: {
      EmailJobStatusChip,
      EmailTestModeBanner,
      TopHeader
    },
    computed: {
      headers() {
        return [
          { text: this.$t('recipient'), value: 'displayName' },
          { text: this.$t('email'), value: 'email' },
          { text: this.$t('type'), value: 'recipientType' },
          { text: this.$t('status'), value: 'status' },
          { text: this.$t('attempts'), value: 'attempts', align: 'end' },
          { text: this.$t('sentAt'), value: 'sentAt' },
          { text: this.$t('error'), value: 'lastError', sortable: false }
        ]
      },
      isRunning() {
        return this.job != null && (this.job.status === 'Pending' || this.job.status === 'Running')
      },
      statusItems() {
        return [ 'Pending', 'Sending', 'Sent', 'Failed', 'Skipped' ].map(s => ({ text: this.$t(s), value: s }))
      }
    },
    async created() {
      this.id = parseInt(this.$route.params.id, 10)
      this.loading = true
      await Promise.all([ this.load(), this.$store.dispatch('loadEmailDesignerData') ])
      this.loading = false

      this.timer = setInterval(() => {
        if (this.isRunning) {
          this.load()
        }
      }, 4000)
    },
    data: () => ({
      deliveries: [],
      deliveriesLoading: false,
      id: null,
      job: null,
      loading: false,
      snackbar: false,
      snackbarText: '',
      statusFilter: null,
      timer: null
    }),
    methods: {
      async load() {
        this.job = await this.$store.dispatch('loadEmailJob', { id: this.id })
        await this.loadDeliveries()
      },
      async loadDeliveries() {
        this.deliveriesLoading = true

        try {
          this.deliveries = await this.$store.dispatch('loadEmailJobDeliveries', { id: this.id, status: this.statusFilter })
        } finally {
          this.deliveriesLoading = false
        }
      },
      async retry() {
        this.job = await this.$store.dispatch('retryEmailJob', { id: this.id })
        this.snackbarText = this.$t('retried')
        this.snackbar = true
        await this.loadDeliveries()
      },
      async cancel() {
        this.job = await this.$store.dispatch('cancelEmailJob', { id: this.id })
        this.snackbarText = this.$t('cancelled')
        this.snackbar = true
        await this.loadDeliveries()
      }
    },
    name: 'EmailJob'
  }
</script>

<style scoped>
  .sf-job__filter {
    max-width: 220px;
  }
</style>

<i18n>
  {
    "de": {
      "back": "Zurück",
      "retryFailed": "Fehlgeschlagene erneut senden",
      "cancel": "Offene abbrechen",
      "refresh": "Aktualisieren",
      "retried": "Fehlgeschlagene Zustellungen wurden neu eingeplant.",
      "cancelled": "Offene Zustellungen wurden abgebrochen.",
      "campaign": "Kampagne",
      "transactional": "Einzelmail",
      "customers": "Kunden",
      "suppliers": "Lieferanten",
      "subject": "Betreff",
      "created": "Erstellt",
      "completed": "Abgeschlossen",
      "createdBy": "Ausgelöst von",
      "total": "Nachrichten",
      "sent": "Gesendet",
      "failed": "Fehlgeschlagen",
      "skipped": "Übersprungen",
      "pending": "Offen",
      "changeToState": "Status nach Versand",
      "deliveries": "Zustellungen",
      "noDeliveries": "Keine Zustellungen.",
      "recipient": "Empfänger",
      "email": "E-Mail",
      "type": "Typ",
      "status": "Status",
      "attempts": "Versuche",
      "sentAt": "Gesendet am",
      "error": "Fehler",
      "nextAttempt": "nächster Versuch",
      "Customer": "Kunde",
      "Guest": "Gast",
      "Supplier": "Lieferant",
      "Pending": "Wartet",
      "Sending": "Wird gesendet",
      "Sent": "Gesendet",
      "Failed": "Fehlgeschlagen",
      "Skipped": "Übersprungen",
      "actions": "Aktionen"
    },
    "en": {
      "back": "Back",
      "retryFailed": "Retry failed",
      "cancel": "Cancel pending",
      "refresh": "Refresh",
      "retried": "Failed deliveries were re-queued.",
      "cancelled": "Pending deliveries were cancelled.",
      "campaign": "Campaign",
      "transactional": "Single email",
      "customers": "Customers",
      "suppliers": "Suppliers",
      "subject": "Subject",
      "created": "Created",
      "completed": "Completed",
      "createdBy": "Triggered by",
      "total": "Messages",
      "sent": "Sent",
      "failed": "Failed",
      "skipped": "Skipped",
      "pending": "Pending",
      "changeToState": "State after sending",
      "deliveries": "Deliveries",
      "noDeliveries": "No deliveries.",
      "recipient": "Recipient",
      "email": "Email",
      "type": "Type",
      "status": "Status",
      "attempts": "Attempts",
      "sentAt": "Sent at",
      "error": "Error",
      "nextAttempt": "next attempt",
      "Customer": "Customer",
      "Guest": "Guest",
      "Supplier": "Supplier",
      "Pending": "Pending",
      "Sending": "Sending",
      "Sent": "Sent",
      "Failed": "Failed",
      "Skipped": "Skipped",
      "actions": "Actions"
    }
  }
</i18n>
